<script setup>
import { i18n } from 'boot/i18n';
import {
  HrErrorBanner,
  HrInfoBanner,
  HrList,
  HrListAddBtn,
  HrListSectionHeader,
  HrListItem,
  HrSubtleBtn,
  useDialog,
} from 'src/design-system';
import { computed, ref, watch } from 'vue';

import { createCoverage } from '../../domain/model/Coverage';
import { toClientProfilePatch, toPolicyholderDemographics } from '../../infrastructure/mappers/policyholderMapper';
import { selectableFundingSources } from '../presenters/selectableFundingSources';
import { COVERAGE_SESSION_METHODS } from '../composables/useClientCoverages';
import CoverageList from './CoverageList.vue';
import EditCoverageDialog from './EditCoverageDialog.vue';
import EndedCoverageList from './EndedCoverageList.vue';

/**
 * The Revenue Cycle context's published interface into the client dialog: capture and maintain a
 * client's coverage — who pays, under what identifiers, for which period, and in what order.
 *
 * A PANEL, not a tab: the tab is the label in the host's tab bar, which the host owns along with
 * the panel wrapper. This is only what renders inside it.
 *
 * It renders a CoverageSession and nothing more; it knows nothing about the client record.
 *
 * The session is created and OWNED BY THE HOST, and passed in. That is not a preference — the
 * host's panel wrapper unmounts when the user switches tabs, so a session owned here would lose
 * every staged edit on the way to another tab and back, and the flush has to happen after the
 * client is saved, which only the host knows about. Both halves of this context's published
 * interface therefore have to be used together: `useClientCoverages()` in the host, this component
 * inside the panel.
 *
 * What makes that explicit rather than merely assumed is the `CoverageSession` typedef the
 * composable returns and the prop validates against — so passing the wrong thing fails loudly at
 * the boundary instead of as an undefined deep in a template.
 */
defineOptions({ name: 'ClientCoveragePanel' });

const props = defineProps({
  /**
   * The editing session from `useClientCoverages`, created and owned by the host.
   * @type {import('../composables/useClientCoverages').CoverageSession}
   */
  coverages: {
    type: Object,
    required: true,
    validator: (session) => COVERAGE_SESSION_METHODS.every((method) => {
      return typeof session?.[method] === 'function';
    }),
  },
  /** The client whose coverage this is; null while the client is being created. */
  clientId: {
    type: String,
    default: null,
  },
  clinicId: {
    type: String,
    required: true,
  },
  /**
   * The client's record AS IT CURRENTLY STANDS IN THE HOST'S EDITOR, unsaved edits included.
   *
   * Supplied rather than read, and the difference is visible. When the client holds their own
   * policy, readiness asks about the client's own record, and this panel sits inside the editor for
   * exactly that record. A stored copy therefore reports gaps the reader has already filled in on
   * the form behind, which reads as the warning being broken rather than stale.
   *
   * A foreign object, so it is translated at this boundary rather than carried inward. Optional: a
   * host that does not supply it falls back to what was read, which is what it did before.
   */
  client: {
    type: Object,
    default: null,
  },
  /**
   * The clinic's country. Supplied by the host rather than read from app state, so this context
   * depends on nothing outside itself, and so a form asking for an address asks the same question
   * the server answers when it stamps one.
   */
  clinicCountry: {
    type: String,
    default: null,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits({
  /** Raised when loading fails; the host dialog owns error notification. */
  error: null,
  /**
   * Corrections to the client's own record, in that document's vocabulary, for the host to merge
   * into the draft it passed in as `client`.
   *
   * Handed back rather than written, because the host is already editing that document and saves it
   * as a whole. Two writers against one record is how one of the two edits silently loses.
   */
  'update:client': null,
});

const session = props.coverages;
const { confirmDestructiveDialog } = useDialog();

// Unwrapped once here so nothing below reaches into another object's refs — the template reads
// like any other component's.
const isLoading = computed(() => session.isLoading.value);
const inSequenceCoverages = computed(() => session.inSequenceCoverages.value);
const endedCoverages = computed(() => session.endedCoverages.value);
const allCoverages = computed(() => session.allCoverages.value);
const fundingSources = computed(() => session.fundingSources.value);
const pausedCoverageIds = computed(() => session.pausedCoverageIds.value);
/**
 * The client as this context sees them, from whatever the host is holding.
 *
 * The mapper is the same one the stored document goes through, because the host's draft is that
 * document in progress. Running it here keeps a second reading of a person's shape out of the panel.
 */
const clientDemographics = computed(() => {
  if (props.client === null || props.clientId === null) {
    return null;
  }

  return toPolicyholderDemographics({ ...props.client, id: props.clientId });
});

// The live client wins over the read one, and only for the client: every other policyholder has no
// editor open behind this panel, so what was read is the freshest thing there is.
const people = computed(() => {
  if (clientDemographics.value === null) {
    return session.people.value;
  }

  return { ...session.people.value, [props.clientId]: clientDemographics.value };
});

const editDialog = ref(null);
const editing = ref(null);

/**
 * The picker offers only active payers — plus, when editing, the one this coverage already points
 * at, so opening an old coverage does not blank its payer.
 *
 * The LISTS get the unfiltered set instead: they use it to resolve names, and a coverage on an
 * inactivated payer is perfectly normal — an ended plan is the likeliest thing to be on a retired
 * funding source. Filtering there would render it as "Unknown funding source".
 */
const dialogFundingSources = computed(() => {
  return selectableFundingSources(fundingSources.value, editing.value?.fundingSourceId ?? null);
});

/** A client usually has one or two payers; more rows would promise a longer list than most have. */
const LOADING_ROWS = 2;

const loadFailed = ref(false);

const isEmpty = computed(() => {
  return !isLoading.value && !loadFailed.value && inSequenceCoverages.value.length === 0;
});

const hasEnded = computed(() => endedCoverages.value.length > 0);

const hasReordered = computed(() => session.hasReordered.value);

async function loadCoverages() {
  loadFailed.value = false;
  try {
    await session.load({ clientId: props.clientId, clinicId: props.clinicId });
  } catch (error) {
    // Never fall through to the empty state: "no coverage recorded" and "we could not read the
    // coverage" look identical, and the first invites a user to re-enter coverage that exists.
    loadFailed.value = true;
    emit('error', error);
  }
} // loadCoverages()

watch(() => props.clientId, loadCoverages, { immediate: true });

function openEditor(coverage) {
  editing.value = coverage;
  // The dialog is rendered by v-if on `editing`, so it does not exist until the next frame.
  requestAnimationFrame(() => editDialog.value?.show());
} // openEditor()

function onAdd() {
  openEditor(createCoverage({ clientId: props.clientId, clinicId: props.clinicId }));
} // onAdd()

/**
 * Removal always confirms, and the confirmation points at the alternative, because "delete" is
 * only ever right for a coverage entered in error. A coverage that was real and has finished
 * should be given an effective end date instead: that keeps it available for billing dates inside
 * its window. The removal itself is a soft delete — see coverageApi.
 */
function onRemove(coverage) {
  const payer = fundingSources.value.find(({ id }) => id === coverage.fundingSourceId)?.name
    ?? i18n.t('coverage.unknownFundingSource');

  confirmDestructiveDialog(
    i18n.t('coverage.deleteConfirmTitle'),
    i18n.t('coverage.deleteConfirmMessage', { payer }),
    () => session.remove(coverage.id),
    { confirmLabel: i18n.t('common.buttonDelete') },
  );
} // onRemove()

// Saving the form never touches the coverage's pause: that decision is taken from the row menu and
// lives in its own aggregate, so an edit cannot disturb it.
function onSaved(coverage) {
  const isExisting = allCoverages.value.some((candidate) => candidate.id === coverage.id);

  if (isExisting) {
    session.update(coverage);
  } else {
    session.add(coverage);
  }
} // onSaved()
</script>

<template>
  <div class="coverage-panel">
    <!-- Placeholder rows rather than a spinner. The section header is real and arrives immediately,
         so the list has a shape before the data does, and rows resolving in place is less movement
         than a spinner being replaced by a list. Two, because a client usually has one or two
         payers and a longer run would promise more than most clients have.

         Not sortable rows: these are not reorderable yet, and drag handles on something that cannot
         be dragged is the wrong affordance. `caption` is set so each reserves the second line the
         real rows use for their effective period. -->
    <hr-list v-if="isLoading">
      <hr-list-section-header>
        {{ i18n.t('coverage.sectionTitle') }}
      </hr-list-section-header>
      <hr-list-item
        v-for="index in LOADING_ROWS"
        :key="index"
        segmented
        pending
        label=""
        caption=""
      />
    </hr-list>

    <hr-error-banner
      v-else-if="loadFailed"
      :message="i18n.t('coverage.loadFailed')"
    />

    <!--
      Non-blocking on purpose. A reorder is staged until the client is saved and is undone by
      dragging back, so gating it behind a confirm would ask the user to accept a consequence that
      has not happened — and anyone arranging several coverages would click through it by reflex.
      The note states what a reorder means and names the alternative, at the moment of intent.
    -->
    <hr-info-banner
      v-else-if="hasReordered"
      :message="i18n.t('coverage.reorderNote')"
      multi-line
      class="q-mb-md"
    >
      <template #action>
        <hr-subtle-btn
          :label="i18n.t('coverage.actionRestoreOrder')"
          data-e2e="btn-restore-coverage-order"
          @click="session.restoreOrder()"
        />
      </template>
    </hr-info-banner>

    <hr-list v-if="!isLoading && !loadFailed">
      <hr-list-section-header>
        {{ i18n.t('coverage.sectionTitle') }}
      </hr-list-section-header>

      <!-- Coverages are the coordination-of-benefits order; the payer billed first is position 0.
           Reorder by dragging the handle (immediate) or long-pressing the row body. When there are
           none, the outlined add button below stands in as the empty state. -->
      <coverage-list
        v-if="!isEmpty"
        :coverages="inSequenceCoverages"
        :paused-coverage-ids="pausedCoverageIds"
        :people="people"
        :funding-sources="fundingSources"
        :disable="readonly"
        @edit="openEditor"
        @remove="onRemove"
        @reorder="session.reorder($event)"
        @move="session.move($event.id, $event.offset)"
        @verify="session.setEligibilityVerified($event.coverageId, $event.verified)"
        @pause="session.setBillingPaused($event.coverageId, $event.paused)"
      />

      <hr-list-add-btn
        v-if="!readonly"
        :variant="isEmpty ? 'outlined' : 'flat'"
        :label="i18n.t('coverage.actionAdd')"
        data-e2e="button-add-coverage"
        @click="onAdd"
      />

      <!-- Past their end date: retained so an old date of service can still be billed against
           them, but out of the sequence entirely — no rank, no drag handle. -->
      <div
        v-if="hasEnded"
        class="coverage-panel__ended"
      >
        <hr-list-section-header>
          {{ i18n.t('coverage.sectionEnded') }}
        </hr-list-section-header>
        <ended-coverage-list
          :coverages="endedCoverages"
          :funding-sources="fundingSources"
          :disable="readonly"
          @edit="openEditor"
          @remove="onRemove"
        />
      </div>
    </hr-list>

    <edit-coverage-dialog
      v-if="editing !== null"
      :key="editing.id ?? 'new'"
      ref="editDialog"
      :coverage="editing"
      :clinic-country="clinicCountry"
      :client-demographics="clientDemographics"
      :funding-sources="dialogFundingSources"
      :siblings="allCoverages"
      @ok="onSaved"
      @update-client="emit('update:client', toClientProfilePatch($event, clientDemographics))"
      @error="emit('error', $event)"
      @hide="editing = null"
    />
  </div>
</template>

<style lang="scss" scoped>

/* Coverage rows are interacted with (tap to edit, drag to reorder), not read as selectable text. */
.coverage-panel {
  user-select: none;
  -webkit-user-select: none;
}

/* Separate the group that has left the billing sequence from the one that is still in it. The
   de-emphasis itself is semantic — those rows carry the `muted` prop. */
.coverage-panel__ended {
  margin-top: 24px;
}

</style>
